import { Fragment, useEffect, useRef, useState } from "react";

import { decode } from "he";
import throttle from "lodash.throttle";

import { Anchor } from "@atlassiansox/bxp-content-framework";

import {
    LinkButton,
    ScrollFade,
    useAnalytics,
    useRoute
} from "../../components";
import { UiGuideModel } from "../../models";
import { renderViewModels } from "../../render-view-model";
import { isBackgroundFieldArray, BIG_NAV_PX_HEIGHT } from "../../styles";
import { smoothScrollTo } from "../../utils";

import { Guide, SubSections, Nav } from "./UiGuide.styles";

export type UiGuideProps = {
    model: UiGuideModel;
};

// eslint-disable-next-line complexity
const UiGuide = ({ model }: UiGuideProps) => {
    const [positionsInitialized, setPositionsInitialized] = useState(false);
    const [indexInitialized, setIndexInitialized] = useState(false);
    const [sectionPositions, setSectionPositions] = useState<number[]>([]);
    const [isSticky, setIsSticky] = useState(false);
    const [scrolledSectionIndex, setScrolledSectionIndex] = useState(-1);
    const [navIsOpen, setNavIsOpen] = useState(false);
    const [navHeight, setNavHeight] = useState<number | undefined>(undefined);
    const route = useRoute();
    const navRef = useRef<HTMLElement>(null);

    const { sendTrackEvent } = useAnalytics();

    //get current chapter
    const { chapters, headerOffset, nextChapterLabel, numberedChapters } =
        model.content;

    const publishedChapters = chapters.filter(
        ({ type }) => type === "uiGuideChapter"
    );
    const chapterIndex = publishedChapters.findIndex(
        (chapter) => chapter.content.route.content.path === route
    );
    const chapter =
        chapterIndex === -1 ? null : publishedChapters[chapterIndex];

    //get bookmarkable sections
    const chapterSections = chapter?.content.sections.filter(
        (section) => section.content.elementId
    );

    //get next chapter
    const nextChapter =
        chapterIndex !== publishedChapters.length - 1
            ? publishedChapters[chapterIndex + 1]
            : null;

    //next chapter button text
    const nextChapterButtonText = nextChapterLabel
        ? decode(nextChapterLabel)
        : nextChapter?.content.title
        ? decode(nextChapter.content.title)
        : undefined;

    //get y coordinates of bookmarkable sections
    const resetSectionPositions = () => {
        const newPositions: number[] = [];
        document.querySelectorAll("section[id]").forEach((element) => {
            if (element instanceof HTMLElement) {
                newPositions.push(element.offsetTop - 122);
            }
        });
        setSectionPositions(newPositions);
        return newPositions;
    };

    const resetNavHeight = () => {
        if (!navRef.current) return;
        const { top } = navRef.current.getBoundingClientRect();
        //set height of ScrollFade (sticky only on lg and up)
        const newNavHeight =
            top <= BIG_NAV_PX_HEIGHT
                ? window.innerHeight - BIG_NAV_PX_HEIGHT
                : undefined;
        setNavHeight(newNavHeight);
    };

    //background color?
    const chapterBackground = isBackgroundFieldArray(
        chapter?.content.background
    )
        ? chapter?.content.background
        : undefined;

    // store current category when rendering chapters in nav
    let currentCategory: string | undefined = undefined;

    useEffect(() => {
        const scrollHandler = throttle(() => {
            const scrollToCheck = globalThis.scrollY + 10; //10px of leeway helps with zoomed-out browsers
            const newSectionIndex =
                sectionPositions[0] && sectionPositions[0] > scrollToCheck
                    ? -1
                    : sectionPositions.filter(
                          (position) => position <= scrollToCheck
                      ).length - 1;
            if (newSectionIndex !== scrolledSectionIndex) {
                //update highlighted nav element
                setScrolledSectionIndex(newSectionIndex);

                //update hash
                const newHash = chapterSections?.[newSectionIndex]
                    ? `#${chapterSections[newSectionIndex]?.content.elementId}`
                    : window.location.pathname;
                history.pushState(null, document.title, newHash);
            }

            //when sidebar changes stickiness, set ScrollFade height (lg and up)
            if (navRef.current) {
                const { top } = navRef.current.getBoundingClientRect();
                if (top <= BIG_NAV_PX_HEIGHT !== isSticky) {
                    setIsSticky(!isSticky);
                    resetNavHeight();
                }
            }
        }, 100);

        const resizeHandler = throttle(() => {
            resetSectionPositions();
            resetNavHeight();
        }, 100);

        //fire on startup
        if (!positionsInitialized) {
            resetSectionPositions();
            setPositionsInitialized(true);
        } else if (!indexInitialized) {
            scrollHandler();
            setIndexInitialized(true);
        }

        //set listeners
        window.addEventListener("resize", resizeHandler);
        document.addEventListener("scroll", scrollHandler);

        //remove listeners
        return () => {
            window.removeEventListener("resize", resizeHandler);
            document.removeEventListener("scroll", scrollHandler);
        };
    }, [
        chapterSections,
        scrolledSectionIndex,
        sectionPositions,
        setScrolledSectionIndex,
        indexInitialized,
        isSticky,
        positionsInitialized
    ]);

    useEffect(() => {
        if (navIsOpen) {
            document.body.classList.add("prevent-scroll");
        } else {
            document.body.classList.remove("prevent-scroll");
        }
        return () => document.body.classList.remove("prevent-scroll");
    }, [navIsOpen]);

    return (
        <Guide
            background={chapterBackground}
            data-nav-open={navIsOpen}
            headerOffset={!!headerOffset}
            onClick={() => setNavIsOpen(false)}
        >
            <aside>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setNavIsOpen(!navIsOpen);
                    }}
                >
                    {chapter?.content.title}
                    <svg width="20" height="12" viewBox="0 0 20 12">
                        <path
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M11.8177 11.2487L18.652 4.35367C19.0879 3.91292 19.3324 3.31805 19.3324 2.69817C19.3324 2.07829 19.0879 1.48341 18.652 1.04267C18.4379 0.825305 18.1828 0.652692 17.9014 0.534871C17.6199 0.417049 17.3179 0.356373 17.0128 0.356373C16.7078 0.356373 16.4057 0.417049 16.1243 0.534871C15.8429 0.652692 15.5877 0.825306 15.3737 1.04267L10.0117 6.449L4.62867 1.01933C4.41403 0.802116 4.15842 0.629658 3.87665 0.511954C3.59487 0.39425 3.29254 0.333643 2.98717 0.333644C2.6818 0.333644 2.37946 0.394251 2.09769 0.511954C1.81591 0.629658 1.5603 0.802116 1.34567 1.01933C0.910345 1.46038 0.666256 2.05514 0.666256 2.67483C0.666256 3.29453 0.910345 3.88929 1.34567 4.33034L8.20567 11.2487C8.68805 11.7287 9.3405 11.9987 10.021 12C10.672 12 11.3207 11.7503 11.8177 11.2487Z"
                        />
                    </svg>
                </button>
                <ScrollFade
                    height={navHeight}
                    style={{
                        position: "sticky",
                        top: BIG_NAV_PX_HEIGHT
                    }}
                >
                    <Nav
                        ref={navRef}
                        data-open={navIsOpen}
                        data-numbered={!!numberedChapters}
                        data-first-chapter={chapterIndex === 0}
                    >
                        {publishedChapters.map((chapter, index) => {
                            let navHeading: JSX.Element | null = null;
                            if (chapter.content.category !== currentCategory) {
                                currentCategory = chapter.content.category;
                                navHeading = (
                                    <h5>{chapter.content.category}</h5>
                                );
                            }

                            return (
                                <Fragment key={index}>
                                    {navHeading}
                                    <Anchor
                                        href={
                                            chapter.content.route.content.path
                                        }
                                        aria-current={
                                            chapterIndex === index
                                                ? "page"
                                                : undefined
                                        }
                                        data-uuid={`${model.id}_navMain`}
                                        onClick={() => {
                                            sendTrackEvent(
                                                "clickedUiGuideNavLink",
                                                {
                                                    event: "clicked",
                                                    eventComponent: "link",
                                                    eventContainer: model.type,
                                                    label: chapter.content
                                                        .title,
                                                    labelEnglish:
                                                        chapter.content
                                                            .entryTitle,
                                                    href: chapter.content.route
                                                        .content.path,
                                                    title: chapter.content
                                                        .title,
                                                    uuid: `${model.id}_navMain`
                                                }
                                            );
                                        }}
                                    >
                                        {decode(chapter.content.title)}
                                    </Anchor>
                                    {chapter.content.route.content.path ===
                                        route &&
                                        !!chapterSections?.length && (
                                            <nav>
                                                {chapterSections.map(
                                                    (section, index) => (
                                                        <Anchor
                                                            className={
                                                                scrolledSectionIndex ===
                                                                index
                                                                    ? "active"
                                                                    : undefined
                                                            }
                                                            href={`#${section.content.elementId}`}
                                                            onClick={(e) => {
                                                                if (!window)
                                                                    return;
                                                                e.preventDefault();

                                                                //send event
                                                                sendTrackEvent(
                                                                    "clickedUiGuideNavLink",
                                                                    {
                                                                        event: "clicked",
                                                                        eventComponent:
                                                                            "link",
                                                                        eventContainer:
                                                                            model.type,
                                                                        label: section
                                                                            .content
                                                                            .guideSectionTitle,
                                                                        labelEnglish:
                                                                            section
                                                                                .content
                                                                                .title,
                                                                        href: `#${section.content.elementId}`,
                                                                        title: section
                                                                            .content
                                                                            .guideSectionTitle,
                                                                        uuid: `${model.id}_navSub`
                                                                    }
                                                                );

                                                                //zoom level may have changed
                                                                const latestPositions =
                                                                    resetSectionPositions();
                                                                const position =
                                                                    latestPositions[
                                                                        index
                                                                    ];
                                                                //smooth scroll to element
                                                                if (position) {
                                                                    smoothScrollTo(
                                                                        position
                                                                    );
                                                                }
                                                            }}
                                                            key={index}
                                                            data-uuid={`${model.id}_navSub`}
                                                        >
                                                            {decode(
                                                                section.content
                                                                    .guideSectionTitle ||
                                                                    ""
                                                            )}
                                                        </Anchor>
                                                    )
                                                )}
                                            </nav>
                                        )}
                                </Fragment>
                            );
                        })}
                    </Nav>
                </ScrollFade>
            </aside>
            <main>
                {chapter?.content.sections &&
                    renderViewModels(chapter.content.sections)}
                {nextChapter && (
                    <nav>
                        <LinkButton
                            appearance="Primary"
                            url={nextChapter.content.route.content.path}
                            decorator="Arrow"
                            onClick={() => {
                                //send event
                                sendTrackEvent(
                                    "clickedUiGuideNextChapterLink",
                                    {
                                        event: "clicked",
                                        eventComponent: "button",
                                        eventContainer: model.type,
                                        label: nextChapterButtonText,
                                        labelEnglish: "Next chapter",
                                        uuid: `${model.id}_next`,
                                        href: nextChapter.content.route.content
                                            .path,
                                        title: nextChapterButtonText
                                    }
                                );
                            }}
                            data-uuid={`${model.id}_next`}
                        >
                            {nextChapterButtonText}
                        </LinkButton>
                    </nav>
                )}
                {chapter?.content.subSections && (
                    <SubSections className="subSections">
                        {renderViewModels(chapter.content.subSections)}
                    </SubSections>
                )}
            </main>
        </Guide>
    );
};

declare global {
    namespace NodeJS {
        interface Global {
            scrollY: number;
        }
    }
}

export default UiGuide;
